import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    ArrowUpRight,
    BookmarkPlus,
    Check,
    Loader2,
    Newspaper,
    NotebookTabs,
    PenLine,
    RefreshCw,
    Search,
    Send,
    SlidersHorizontal,
    Sparkles,
    X,
} from "lucide-react";
import FooterNav from "../components/FooterNav";
import { createNote } from "../api/notes";
import { hasIdentity, requireIdentity } from "../utils/identityGate";
import { fetchPulse, type PulseNewsItem, type PulsePage } from "../api/pulse";
import { fetchJoinedSpaces, shareNoteToSpace, type Space } from "../api/spaces";
import { formatBlackspaceDateTime } from "../utils/time";

const PAGE_SIZE = 12;

function timeLabel(value?: string) {
    return formatBlackspaceDateTime(value);
}

function escapeHtml(value?: string) {
    return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

function articleNoteContent(item: PulseNewsItem) {
    const summary = item.summary?.trim() || "No summary available.";
    return [
        `<h1>${escapeHtml(item.title)}</h1>`,
        `<p><strong>Source:</strong> ${escapeHtml(item.source)}</p>`,
        `<p><strong>Published:</strong> ${escapeHtml(timeLabel(item.publishedAt))}</p>`,
        `<p><strong>Topic:</strong> ${escapeHtml(item.topicLabel || item.topic || "Pulse")}</p>`,
        `<h2>Summary</h2>`,
        `<p>${escapeHtml(summary)}</p>`,
        `<h2>Original Story</h2>`,
        `<p><a href="${escapeHtml(item.url)}" target="_blank" rel="noreferrer">Open original story</a></p>`,
    ].filter(Boolean).join("");
}

function postDraft(item: PulseNewsItem) {
    return `${item.title}\n\n${item.summary || ""}\n\n${item.url}`;
}

export default function Pulse() {
    const navigate = useNavigate();
    const isAnonymous = !hasIdentity();
    const location = useLocation();
    const routeState = location.state as { from?: string; reopenBlackspaceMenuOnBack?: boolean } | null;
    const originRef = useRef<string | null>(routeState?.from || null);
    const reopenMenuOnBackRef = useRef(Boolean(routeState?.reopenBlackspaceMenuOnBack));
    const [page, setPage] = useState<PulsePage | null>(null);
    const [news, setNews] = useState<PulseNewsItem[]>([]);
    const [spaces, setSpaces] = useState<Space[]>([]);
    const [topic, setTopic] = useState("all");
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [searchOpen, setSearchOpen] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [shareTargetId, setShareTargetId] = useState("");
    const [notice, setNotice] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 240);
        return () => window.clearTimeout(timer);
    }, [query]);

    const load = useCallback(async (cursor: string | null = null) => {
        cursor ? setLoadingMore(true) : setLoading(true);
        setError(null);
        try {
            const result = await fetchPulse({ topic, q: debouncedQuery, cursor, limit: PAGE_SIZE });
            setPage(result);
            setNews((current) => cursor ? [...current, ...result.news] : result.news);
            setNextCursor(result.nextCursor);
            setHasMore(result.hasMore);
        } catch (err: any) {
            setError(err?.message || "Could not load Pulse.");
        } finally {
            cursor ? setLoadingMore(false) : setLoading(false);
        }
    }, [debouncedQuery, topic]);

    useEffect(() => {
        load(null);
    }, [load]);

    useEffect(() => {
        fetchJoinedSpaces({ limit: 40 })
            .then((result) => {
                setSpaces(result.spaces || []);
                setShareTargetId((current) => current || result.spaces?.[0]?.id || "");
            })
            .catch(() => {
                // Pulse still works without joined spaces.
            });
    }, []);

    const brief = useMemo(() => page?.brief || [], [page]);
    const topics = useMemo(() => page?.topics?.length ? page.topics : [{ id: "all", label: "All" }], [page]);

    async function saveToVault(item: PulseNewsItem, silent = false) {
        if (isAnonymous) {
            requireIdentity("save this story to Vault");
            return null;
        }
        setBusyId(`vault:${item.id}`);
        setError(null);
        try {
            const note = await createNote({
                title: item.title.slice(0, 180),
                content: articleNoteContent(item),
                type: "rich",
                color: "#22d3ee",
                tags: ["pulse", item.topic, item.source.toLowerCase().replace(/\s+/g, "-")],
                folder: null,
            });
            if (!silent) setNotice("Saved to Vault.");
            return note;
        } catch (err: any) {
            setError(err?.message || "Could not save to Vault.");
            return null;
        } finally {
            setBusyId(null);
        }
    }

    async function shareToSpace(item: PulseNewsItem) {
        if (isAnonymous) return void requireIdentity("share this story to a Space");
        if (!shareTargetId) {
            setError("Join a Space before sharing Pulse items.");
            return;
        }
        setBusyId(`space:${item.id}`);
        setError(null);
        try {
            const note = await saveToVault(item, true);
            if (!note) return;
            await shareNoteToSpace(shareTargetId, {
                noteId: note.id,
                kind: "discussion",
                syncMode: "frozen",
                permissions: { allowComments: true, allowCopy: true, allowRemix: true },
            });
            setNotice("Shared to Space materials.");
        } catch (err: any) {
            setError(err?.message || "Could not share to Space.");
        } finally {
            setBusyId(null);
        }
    }

    function startPost(item: PulseNewsItem) {
        if (isAnonymous) return void requireIdentity("write a post");
        navigate("/home", { state: { pulseDraft: postDraft(item), restoreFeed: true } });
    }

    const goBack = useCallback(() => {
        const currentRoute = `${location.pathname}${location.search}${location.hash}`;
        if (originRef.current && originRef.current !== currentRoute) {
            navigate(originRef.current, { state: reopenMenuOnBackRef.current ? { reopenBlackspaceMenu: true } : undefined });
            return;
        }
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }
        navigate("/home");
    }, [location.hash, location.pathname, location.search, navigate]);

    return (
        <div className="bs-page min-h-screen overflow-hidden bg-black text-white">
            <main className="h-screen overflow-y-auto px-4 pb-32 pt-5 sm:px-8">
                <div className="mx-auto max-w-6xl space-y-4">
                    <header className="relative overflow-hidden rounded-[1.75rem] border border-cyan-400/15 bg-black/70 p-4 shadow-[0_0_90px_rgba(8,145,178,0.12)]">
                        <div className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(circle_at_12%_0%,rgba(8,145,178,0.24),transparent_34%),linear-gradient(130deg,rgba(8,145,178,0.12),transparent_45%,rgba(168,85,247,0.10))]" />
                        <div className="relative flex items-center justify-between gap-3">
                            <div className="flex min-w-0 items-center gap-3">
                                <button type="button" onClick={goBack} className="bs-premium-header-action grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white/[0.04] text-neutral-200 shadow-[0_0_30px_rgba(8,145,178,0.12)] hover:text-cyan-200" title="Back" aria-label="Back">
                                    <ArrowLeft size={20} />
                                </button>
                                <h1 className="bs-message-page-title truncate">Pulse</h1>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                                <button type="button" onClick={() => setSearchOpen((v) => !v)} className="bs-premium-header-action grid h-12 w-12 place-items-center rounded-2xl bg-white/[0.04] text-neutral-200 shadow-[0_0_30px_rgba(8,145,178,0.12)] hover:text-cyan-200" title="Search Pulse">
                                    <Search size={20} />
                                </button>
                                <button type="button" onClick={() => setFiltersOpen((v) => !v)} className="bs-premium-header-action grid h-12 w-12 place-items-center rounded-2xl bg-white/[0.04] text-neutral-200 shadow-[0_0_30px_rgba(168,85,247,0.10)] hover:text-white" title="Filter Pulse">
                                    <SlidersHorizontal size={20} />
                                </button>
                                <button type="button" onClick={() => load(null)} disabled={loading} className="bs-premium-header-action grid h-12 w-12 place-items-center rounded-2xl bg-white/[0.04] text-neutral-200 shadow-[0_0_30px_rgba(8,145,178,0.12)] hover:text-cyan-200 disabled:opacity-50" title="Refresh Pulse">
                                    <RefreshCw size={19} className={loading ? "animate-spin" : ""} />
                                </button>
                            </div>
                        </div>

                        {searchOpen && (
                            <div className="relative mt-4">
                                <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-300" />
                                <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search headlines, sources, topics" className="h-12 w-full rounded-2xl border border-white/10 bg-black/70 pl-11 pr-11 text-sm font-bold text-white outline-none focus:border-cyan-700" />
                                {query && <button type="button" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white" title="Clear search"><X size={17} /></button>}
                            </div>
                        )}

                        {filtersOpen && (
                            <div className="relative mt-4 space-y-3 rounded-2xl bg-white/[0.035] p-3">
                                <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                    {topics.map((item) => (
                                        <button key={item.id} type="button" onClick={() => setTopic(item.id)} className={`h-10 shrink-0 rounded-xl px-4 text-xs font-black transition ${topic === item.id ? "bg-cyan-400/12 text-cyan-100 shadow-[0_0_24px_rgba(34,211,238,0.18)]" : "bg-black/45 text-neutral-500 hover:text-neutral-200"}`}>
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                                {spaces.length > 0 && (
                                    <select value={shareTargetId} onChange={(event) => setShareTargetId(event.target.value)} className="h-11 w-full rounded-2xl border border-white/10 bg-black/55 px-3 text-xs font-black text-neutral-200 outline-none focus:border-cyan-700" title="Default Space for sharing">
                                        {spaces.map((space) => <option key={space.id} value={space.id}>Share to {space.title}</option>)}
                                    </select>
                                )}
                            </div>
                        )}

                        <div className="relative mt-4 flex flex-wrap items-center gap-2 text-xs font-bold text-neutral-500">
                            <span>{page?.total || 0} stories</span>
                            <span>·</span>
                            <span>{page?.source || "World"}</span>
                            <span>·</span>
                            <span>{page?.updatedAt ? `Updated ${timeLabel(page.updatedAt)}` : "Updating"}</span>
                            {page?.stale && <span className="text-amber-300">stale cache</span>}
                        </div>
                    </header>

                    {notice && <div className="rounded-2xl border border-emerald-400/20 bg-emerald-950/25 px-4 py-3 text-sm font-bold text-emerald-100">{notice}</div>}
                    {error && <div role="alert" className="rounded-2xl border border-red-400/20 bg-red-950/25 px-4 py-3 text-sm font-bold text-red-100">{error}</div>}

                    {brief.length > 0 && (
                        <section className="rounded-[1.5rem] bg-white/[0.035] p-4 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]">
                            <div className="mb-3 flex items-center justify-between">
                                <h2 className="flex items-center gap-2 text-sm font-black text-white"><Sparkles size={16} className="text-cyan-300" /> Today's brief</h2>
                                <span className="text-xs font-bold text-neutral-600">{brief.length} picks</span>
                            </div>
                            <div className="grid gap-2 md:grid-cols-5">
                                {brief.map((item) => (
                                    <button key={item.id} type="button" onClick={() => window.open(item.url, "_blank", "noopener,noreferrer")} className="rounded-2xl bg-black/45 p-3 text-left transition hover:bg-cyan-400/10">
                                        <p className="line-clamp-3 text-xs font-black leading-5 text-white">{item.title}</p>
                                        <p className="mt-2 text-[11px] font-bold text-cyan-300">{item.topicLabel}</p>
                                    </button>
                                ))}
                            </div>
                        </section>
                    )}

                    <section className="space-y-3">
                        <div className="space-y-3">
                            <h2 className="flex items-center gap-2 text-sm font-black text-neutral-200"><Newspaper size={16} className="text-emerald-300" /> World feed</h2>
                            {news.map((item) => (
                                <article key={item.id} className="group relative overflow-hidden rounded-[1.35rem] bg-white/[0.032] p-3.5 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)] transition hover:bg-white/[0.052] sm:p-4">
                                    <div className="pointer-events-none absolute inset-0 opacity-0 transition group-hover:opacity-100 [background:radial-gradient(circle_at_12%_0%,rgba(8,145,178,0.16),transparent_34%)]" />
                                    <div className="relative min-w-0">
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2 text-[11px] font-black text-neutral-500">
                                                <span className="rounded-full bg-cyan-400/10 px-2 py-1 text-cyan-200">{item.topicLabel}</span>
                                                <span>{item.source}</span>
                                                <span>·</span>
                                                <span>{timeLabel(item.publishedAt)}</span>
                                            </div>
                                            <h3 className="mt-2 text-[0.95rem] font-black leading-5 text-white sm:text-base sm:leading-6">{item.title}</h3>
                                            {item.summary && <p className="mt-2 line-clamp-2 text-[0.82rem] leading-5 text-neutral-400">{item.summary}</p>}
                                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                                <button type="button" onClick={() => window.open(item.url, "_blank", "noopener,noreferrer")} className="grid h-9 w-9 place-items-center rounded-xl bg-white/[0.04] text-neutral-200 transition hover:text-cyan-200" title="Open article" aria-label="Open article"><ArrowUpRight size={16} /></button>
                                                <button type="button" onClick={() => saveToVault(item)} disabled={busyId === `vault:${item.id}`} className="grid h-9 w-9 place-items-center rounded-xl bg-violet-400/10 text-violet-100 transition hover:bg-violet-400/15 disabled:opacity-50" title="Save summary to Vault" aria-label="Save summary to Vault">{busyId === `vault:${item.id}` ? <Loader2 size={16} className="animate-spin" /> : <BookmarkPlus size={16} />}</button>
                                                <button type="button" onClick={() => shareToSpace(item)} disabled={busyId === `space:${item.id}`} className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-400/10 text-emerald-100 transition hover:bg-emerald-400/15 disabled:opacity-50" title="Share to Space" aria-label="Share to Space">{busyId === `space:${item.id}` ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</button>
                                                <button type="button" onClick={() => startPost(item)} className="grid h-9 w-9 place-items-center rounded-xl bg-cyan-400/10 text-cyan-100 transition hover:bg-cyan-400/15" title="Start post" aria-label="Start post"><PenLine size={16} /></button>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            ))}

                            {loading && news.length === 0 && (
                                <div className="space-y-3">
                                    {Array.from({ length: 5 }).map((_, index) => <div key={index} className="h-36 animate-pulse rounded-3xl bg-white/[0.035]" />)}
                                </div>
                            )}

                            {!loading && news.length === 0 && (
                                <div className="rounded-3xl bg-white/[0.035] p-10 text-center">
                                    <NotebookTabs size={30} className="mx-auto mb-3 text-neutral-600" />
                                    <h2 className="text-lg font-black text-white">No Pulse stories found</h2>
                                    <p className="mt-2 text-sm text-neutral-500">Try another topic or search.</p>
                                </div>
                            )}

                            {hasMore && (
                                <button type="button" onClick={() => load(nextCursor)} disabled={loadingMore} className="mx-auto flex h-11 items-center gap-2 rounded-2xl bg-cyan-400/10 px-5 text-sm font-black text-cyan-100">
                                    {loadingMore ? <Loader2 size={17} className="animate-spin" /> : <Check size={17} />}
                                    Show more
                                </button>
                            )}
                        </div>
                    </section>
                </div>
            </main>
            <FooterNav />
        </div>
    );
}
